import { useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { DiscardDialog } from "@/components/DiscardDialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  createAppointmentService,
  updateAppointmentService,
  getSettings,
  listServiceStaff,
  listStoreTargetDeals,
} from "@/lib/api/admin";
import type { AppointmentService } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TextInput } from "@/components/TextInput";
import { Textarea } from "@/components/Textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/Spinner";
import { parseCents, formatCents } from "@/lib/utils/number";
import { BYPASS_MAX_PRICE_DOLLARS } from "@/lib/constants";
import { ScheduleInput } from "@/components/schedule/ScheduleInput";
import {
  scheduleToForm,
  formToSchedule,
  validateRawSchedule,
  type ScheduleFormState,
} from "@/lib/utils/schedule";

function makeSchema(maxPriceDollars: number) {
  return z.object({
    name: z.string().min(1, "Name is required.").max(100, "Name must be 100 characters or fewer."),
    description: z.string().max(700, "Description must be 700 characters or fewer."),
    durationMinutes: z
      .string()
      .refine(
        (v) => !isNaN(parseInt(v, 10)) && parseInt(v, 10) > 0,
        "Duration must be a positive number of minutes."
      ),
    price: z
      .string()
      .refine(
        (v) => !isNaN(parseFloat(v)) && parseFloat(v) > 0,
        "Price must be greater than $0.00."
      )
      .refine(
        (v) => parseFloat(v) <= maxPriceDollars,
        `Price cannot exceed $${maxPriceDollars.toLocaleString()}.`
      ),
    active: z.enum(["active", "inactive"]),
  });
}

type FormValues = z.infer<ReturnType<typeof makeSchema>>;

type AppointmentServiceFormDialogProps = {
  open: boolean;
  onClose: () => void;
  storeId: string;
  storeCategory: string;
  priceLimitBypass: boolean;
  service?: AppointmentService;
};

export function AppointmentServiceFormDialog({
  open,
  onClose,
  storeId,
  storeCategory,
  priceLimitBypass,
  service,
}: AppointmentServiceFormDialogProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  // Availability -- only consulted once this service has zero staff offering it (see
  // AppointmentStaffSummary/"Offered by" below); a staffed service's slots come from its
  // staff's own schedules instead.
  const initialSchedule = useMemo(() => scheduleToForm(service?.weekly_schedule), [service]);
  const [schedule, setSchedule] = useState<ScheduleFormState>(initialSchedule);
  const [scheduleErrors, setScheduleErrors] = useState<Record<string, string>>({});
  const [useStoreHours, setUseStoreHours] = useState(
    () => !service?.weekly_schedule || Object.keys(service.weekly_schedule).length === 0
  );
  const [capacityMode, setCapacityMode] = useState<"unlimited" | "limited">(
    service?.capacity ? "limited" : "unlimited"
  );
  const [capacityValue, setCapacityValue] = useState(
    service?.capacity ? String(service.capacity) : ""
  );

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: () => getSettings(user!),
    enabled: !!user,
  });

  const { data: offeredByStaff, isLoading: offeredByStaffLoading } = useQuery({
    queryKey: ["appointment-service-staff", storeId, service?.id],
    queryFn: () => listServiceStaff(user!, storeId, service!.id),
    enabled: !!user && open && !!service,
  });

  const { data: targetDeals, isLoading: targetDealsLoading } = useQuery({
    queryKey: ["target-deals", storeId],
    queryFn: () => listStoreTargetDeals(user!, storeId),
    enabled: !!user && open && !!service,
  });
  const associatedDeals = (targetDeals ?? []).filter((d) =>
    d.appointment_service_ids?.includes(service?.id ?? "")
  );

  const maxPriceDollars = priceLimitBypass
    ? BYPASS_MAX_PRICE_DOLLARS
    : storeCategory === "nightclub"
      ? (settings?.nightclub_max_price_cents ?? 50000) / 100
      : (settings?.default_max_price_cents ?? 30000) / 100;

  const schema = useMemo(() => makeSchema(maxPriceDollars), [maxPriceDollars]);

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: service?.name ?? "",
      description: service?.description ?? "",
      durationMinutes: service ? String(service.duration_minutes) : "",
      price: service ? (service.price_cents / 100).toFixed(2) : "",
      active: service?.active === false ? "inactive" : "active",
    },
  });

  const priceField = register("price");

  const initialUseStoreHours =
    !service?.weekly_schedule || Object.keys(service.weekly_schedule).length === 0;
  const scheduleDirty =
    useStoreHours !== initialUseStoreHours ||
    (!useStoreHours && JSON.stringify(schedule) !== JSON.stringify(initialSchedule));
  const initialCapacityMode = service?.capacity ? "limited" : "unlimited";
  const initialCapacityValue = service?.capacity ? String(service.capacity) : "";
  const capacityDirty =
    capacityMode !== initialCapacityMode ||
    (capacityMode === "limited" && capacityValue !== initialCapacityValue);

  function tryClose() {
    if (isDirty || scheduleDirty || capacityDirty) {
      setDiscardOpen(true);
    } else {
      onClose();
    }
  }

  async function onSubmit(values: FormValues) {
    if (!useStoreHours) {
      const errs = validateRawSchedule(formToSchedule(schedule));
      if (errs) {
        setScheduleErrors(errs);
        return;
      }
    }
    setScheduleErrors({});

    let capacity: number | null = null;
    if (capacityMode === "limited") {
      const parsed = parseInt(capacityValue, 10);
      if (isNaN(parsed) || parsed < 1) {
        toast.error("Capacity must be a positive number.");
        return;
      }
      capacity = parsed;
    }

    setIsSubmitting(true);
    try {
      const payload: Partial<AppointmentService> = {
        name: values.name.trim(),
        description: values.description.trim() || undefined,
        duration_minutes: parseInt(values.durationMinutes, 10),
        price_cents: parseCents(values.price),
        weekly_schedule: useStoreHours ? {} : formToSchedule(schedule),
        capacity,
        active: values.active === "active",
      };

      if (service) {
        await updateAppointmentService(user!, storeId, service.id, payload);
      } else {
        await createAppointmentService(user!, storeId, payload);
      }

      await qc.invalidateQueries({ queryKey: ["appointment-services", storeId] });
      toast.success(service ? "Service updated." : "Service created.");
      onClose();
    } catch (e) {
      setError("root", { message: (e as Error).message });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) tryClose();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{service ? "Edit Service" : "New Service"}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5 py-2">
            <TextInput
              id="asv-name"
              label="Name *"
              placeholder="Haircut"
              maxLength={100}
              error={errors.name?.message}
              {...register("name")}
            />

            <Textarea
              id="asv-desc"
              label="Description"
              rows={2}
              maxLength={700}
              placeholder="Optional description"
              error={errors.description?.message}
              {...register("description")}
            />

            <div className="space-y-1">
              <Label htmlFor="asv-duration">Duration (minutes) *</Label>
              <Input
                id="asv-duration"
                type="text"
                inputMode="numeric"
                placeholder="30"
                aria-invalid={!!errors.durationMinutes}
                {...register("durationMinutes")}
              />
              {errors.durationMinutes && (
                <p className="text-xs text-destructive">{errors.durationMinutes.message}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="asv-price">Price ($) *</Label>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  $
                </span>
                <Input
                  id="asv-price"
                  className="pl-6"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  aria-invalid={!!errors.price}
                  {...priceField}
                  onChange={(e) => {
                    e.target.value = e.target.value
                      .replace(/[^\d.]/g, "")
                      .replace(/(\..*)\./g, "$1");
                    priceField.onChange(e);
                  }}
                />
              </div>
              {errors.price ? (
                <p className="text-xs text-destructive">{errors.price.message}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Max ${maxPriceDollars.toLocaleString()}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Availability</Label>
              <p className="text-xs text-muted-foreground">
                Only used once this service has no staff offering it — a staffed service's
                availability comes from its staff's own schedules instead.
              </p>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={useStoreHours}
                  onCheckedChange={(checked) => setUseStoreHours(!!checked)}
                  aria-label="Use store hours"
                />
                <span className="text-sm">Use store hours</span>
              </label>
              {!useStoreHours && (
                <ScheduleInput
                  value={schedule}
                  onChange={(v) => {
                    setSchedule(v);
                    setScheduleErrors({});
                  }}
                  errors={scheduleErrors}
                />
              )}
            </div>

            <div className="space-y-1">
              <Label>Capacity</Label>
              <SegmentedToggle
                value={capacityMode}
                onChange={(v) => setCapacityMode(v as "unlimited" | "limited")}
                options={[
                  { value: "unlimited", label: "Unlimited" },
                  { value: "limited", label: "Limit simultaneous bookings" },
                ]}
              />
              {capacityMode === "limited" && (
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 3"
                  value={capacityValue}
                  onChange={(e) => setCapacityValue(e.target.value.replace(/\D/g, ""))}
                />
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              To discount this service, include it in a Target Deal.
            </p>

            {service && (
              <div className="space-y-1">
                <Label>Target Deals</Label>
                {targetDealsLoading ? (
                  <div className="flex items-center gap-2 py-1">
                    <Spinner className="size-3.5 border-primary" />
                    <span className="text-xs text-muted-foreground">Loading target deals…</span>
                  </div>
                ) : associatedDeals.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-1">
                    Not included in any Target Deal yet.
                  </p>
                ) : (
                  <div className="border rounded-md divide-y">
                    {associatedDeals.map((deal) => (
                      <div key={deal.id} className="flex items-center gap-2 px-3 py-2">
                        <span className="flex-1 min-w-0 text-sm truncate">{deal.name}</span>
                        <span className="text-xs font-medium text-muted-foreground shrink-0">
                          {deal.discount_type === "fixed_price"
                            ? `${formatCents(deal.fixed_price_cents ?? 0)} each`
                            : `${Math.round(deal.discount_percent ?? 0)}% off`}
                        </span>
                        <StatusBadge status={deal.status} className="shrink-0" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {service &&
              (offeredByStaffLoading ? (
                <div className="flex items-center gap-2">
                  <Spinner className="size-3.5 border-primary" />
                  <span className="text-xs text-muted-foreground">
                    Loading staff who offer this service…
                  </span>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {offeredByStaff && offeredByStaff.length > 0
                    ? `Offered by: ${offeredByStaff.map((s) => s.name).join(", ")}`
                    : "Not offered by any staff yet."}
                </p>
              ))}

            <div className="space-y-1">
              <Label>Status</Label>
              <Controller
                control={control}
                name="active"
                render={({ field }) => (
                  <SegmentedToggle
                    value={field.value}
                    onChange={field.onChange}
                    options={[
                      { value: "active", label: "Active" },
                      { value: "inactive", label: "Inactive" },
                    ]}
                  />
                )}
              />
            </div>

            {errors.root?.message && (
              <p className="text-sm text-destructive">{errors.root.message}</p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={tryClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Spinner className="size-4 border-background" />
                ) : service ? (
                  "Save"
                ) : (
                  "Create"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DiscardDialog
        open={discardOpen}
        onConfirm={() => {
          setDiscardOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
}
